import './index.scss';
import { Trash } from 'react-feather';
import {
  useCloseOldModal,
  useModalData,
  useOpenOldModal,
} from '../../../../store/modal/modalHooks';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Toast from '../../../shared/Toast/Toast';
import { useTranslation } from 'react-i18next';
import 'cleave.js/dist/addons/cleave-phone.us';
import { InviteGuestForm } from '../InviteGuestForm';
import { useProfile } from '../../../../store/Auth/authHooks';
import {
  useFetchSpacesManagementSpacesByCompany,
  useSpacesManagementCompanySpaces,
} from '../../../../store/SpacesManagement/spacesManagementHooks';
import {
  useGuestById,
  useGuestsManagementRemoveGuest,
  useGuestsManagementUpdateGuest,
} from '../../../../store/guests-management/guestsManagementHooks';
import { ModalComponentEnum } from '../../../../store/modal/modalTypes';
import { Button, Form, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { GuestManagementUpdateGuest } from '../../../../store/guests-management/guestsManagementTypes';
import { SpecialNeedEnum } from '../../../../API/guest-management/enum/special-need.enum';

const EditGuestModal = () => {
  const profile = useProfile();
  const { t } = useTranslation();
  const openModal = useOpenOldModal();
  const modalData = useModalData();
  const closeModal = useCloseOldModal();
  const guest = useGuestById(modalData?.data?.id);
  const updateGuest = useGuestsManagementUpdateGuest();
  const removeGuest = useGuestsManagementRemoveGuest();
  const companySpaces = useSpacesManagementCompanySpaces();
  const fetchCompanySpaces = useFetchSpacesManagementSpacesByCompany();

  useEffect(() => {
    fetchCompanySpaces().finally();
  }, [profile?.company?.id!]);

  const spaces = [
    ...new Set(
      companySpaces.map(
        (space) => ` ${space.building.name}, ${space.floor?.number}`
      )
    ),
  ];

  interface formValues {
    fullName: string;
    phone: string;
    email: string;
    arrivingOn: Date[];
    companyId: number;
    allowParking: boolean;
    licenseNumber: string;
    message: string;
    building: number;
    parkingNumber: string;
    parkingFloorNumber: string;
    guestFloors: string[];
    orderForAnother: boolean;
    invitedName: string;
    invitedNameFreeText: string;
    specialNeed?: SpecialNeedEnum;
    space: number;
  }

  useEffect(() => {
    reset({
      fullName: guest?.fullName || '',
      phone: guest?.phone || '',
      email: guest?.email || '',
      arrivingOn: guest?.arrivingOn ? [new Date(guest.arrivingOn)] : '',
      companyId: guest?.companyId,
      allowParking: guest?.allowParking || false,
      licenseNumber: guest?.licenseNumber || '',
      message: guest?.message || '',
      building: guest?.buildings ? guest?.buildings[0]?.id : 0,
      parkingNumber: guest?.parkingNumber || '',
      parkingFloorNumber: guest?.parkingFloorNumber || '',
      guestFloors: guest?.guestFloors || [],
      orderForAnother: Boolean(guest?.inviterName),
      invitedName: '',
      invitedNameFreeText: guest?.inviterName || '',
      specialNeed: guest?.specialNeed || SpecialNeedEnum.NORMAL,
      space:
        companySpaces.find((space) => space.floor?.id === guest?.place.floorId)
          ?.id || -1,
    });
  }, [guest, companySpaces]);

  const defaultValues: formValues = {
    fullName: guest?.fullName || '',
    phone: guest?.phone || '',
    email: guest?.email || '',
    arrivingOn: guest?.arrivingOn ? [new Date(guest.arrivingOn)] : [new Date()],
    companyId: guest?.companyId || -1,
    allowParking: guest?.allowParking || false,
    licenseNumber: guest?.licenseNumber || '',
    message: guest?.message || '',
    building: guest?.buildings ? guest?.buildings[0]?.id : -1,
    parkingNumber: guest?.parkingNumber || '',
    parkingFloorNumber: guest?.parkingFloorNumber || '',
    guestFloors: guest?.guestFloors || [],
    orderForAnother: Boolean(guest?.inviterName),
    invitedName: '',
    invitedNameFreeText: guest?.inviterName || '',
    specialNeed: guest?.specialNeed || SpecialNeedEnum.NORMAL,
    space:
      companySpaces.find((space) => space.floor?.id === guest?.place.floorId)
        ?.id || -1,
  };

  const form = useForm({});
  const {
    reset,
    handleSubmit,
    formState: { errors },
  } = form;

  const onDeleteGuest = () => {
    openModal(ModalComponentEnum.QUESTION_REMOVE_GUEST_MODAL, {}, {}, () => {
      if (guest?.id) {
        removeGuest(guest.id)
          .then((response) => {
            closeModal();
            Toast('success-request', t('VISITOR_HAS_BEEN_DELETED'), t);
          })
          .catch((error) => {
            Toast('error', error.response.data.error.message);
          });
      }
    });
  };

  const onSubmit = (data: formValues) => {
    if (guest?.id) {
      //TODO refactor
      const timeZoneOffset = 1000 * 60 * 60 * 8; //8hrs
      const guestSpace = companySpaces.find((space) => space.id === data.space);
      const space = {
        location_id: guestSpace?.building.locationId || -1,
        building_id: guestSpace?.building.id || -1,
        floor_id: guestSpace?.floorId || -1,
      };

      const newData: GuestManagementUpdateGuest = {
        ...data,
        licenseNumber: data.licenseNumber.replace(/ /g, ''),
        arrivingOn:
          new Date(data.arrivingOn[0].setHours(0, 0, 0, 0)).getTime() +
          timeZoneOffset,
        companyId: +data.companyId,
        guestFloors: guest?.guestFloors || spaces,
        specialNeed: data.specialNeed || SpecialNeedEnum.NORMAL,
      };

      updateGuest(
        guest.id,
        newData,
        space,
        (isSuccessful, message) => {
          const toastType = isSuccessful ? 'success-request' : 'error';
          Toast(toastType, message, t);
        },
        t
      ).then(() => closeModal());
    }
  };

  return (
    <div className="invite-new-guest-modal">
      <div className="form-modal">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <p className="form-header-title">{t('EDIT_VISITOR')}</p>
          </ModalHeader>
          <ModalBody>
            <InviteGuestForm form={form} />
          </ModalBody>
          <ModalFooter className="update-guest-buttons">
            <div>
              <Button className="delete-guest" color="" onClick={onDeleteGuest}>
                <Trash size={10} />
                {t('DELETE_VISITOR')}
              </Button>
            </div>
            <div>
              <Button type="button" color="" onClick={closeModal}>
                {t('CLOSE_BUTTON')}
              </Button>{' '}
              <Button type="submit" color="primary">
                {t('SAVE_CHANGES')}
              </Button>{' '}
            </div>
          </ModalFooter>
        </Form>
      </div>
    </div>
  );
};

export default EditGuestModal;
